/**
 * PlantEnums Component
 * 
 * Main component for managing plant enumerations (plant types/varieties).
 * Displays a list of all plant enums with the ability to:
 * - Add new plant varieties
 * - View existing plants in a grid layout
 * - Edit or delete plant entries via the card actions
 * 
 * The component uses Firestore real-time listener to keep the plant list
 * synchronized with the database.
 * 
 * Layout adapts based on device width:
 * - Desktop (>800px): 2-column grid
 * - Mobile (<=800px): Single column
 */
import React, { useEffect, useState } from "react";

import {
  onSnapshot,
  collection,
  query,
} from "firebase/firestore";
import { firebaseStore } from "../../../components/firebase/firestore";
import CircularProgress from "@mui/material/CircularProgress";

import { useNavigate } from "react-router-dom";

import { useDeviceState } from "../../../components/utils/globalStates";

import AddPlantEnum from "./components/AddPlantEnum";
import CardsPlantEnumList from "../../../components/ui/cards/CardsPlantEnumList";

import "./PlantEnums.css";

function PlantEnums() {
  const navigate = useNavigate();
  const deviceState = useDeviceState();
  
  // State for storing the array of plant enums from Firestore
  const [plantEnums, setPlantEnums] = useState<any[]>([]);
  
  // Loading state for initial data fetch
  const [searchLoading, setSearchLoading] = useState(true);

  /**
   * Set up Firestore real-time listener for plantEnums collection
   * Updates component state whenever data changes in Firestore
   * Automatically unsubscribes on component unmount
   */
  useEffect(() => {
    const q = query(collection(firebaseStore, "plantEnums"));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const tempPlantEnums: any[] = [];
      querySnapshot.forEach((doc) => {
        // Combine document data with its ID for reference
        tempPlantEnums.push({...doc.data(), id: doc.id});
      });
      setPlantEnums(tempPlantEnums);
      setSearchLoading(false);
    });
    
    // Cleanup: Unsubscribe from listener when component unmounts
    return unsubscribe;
  }, []);

  return (
    <div className="PlantEnums">
      {/* Add New Plant Enum Button/Dialog */}
      <AddPlantEnum />
      
      {/* Loading State: Show spinner while fetching data */}
      {searchLoading ? (
        <div className="PlantEnumsSearchLoading">
          <CircularProgress color="inherit" />
        </div>
      ) : (
        /* Plant Enums Grid: Responsive layout that adapts to screen size */
        <div className="PlantEnumsList">
          {plantEnums.map((plantEnum, index) => (
            <CardsPlantEnumList
              key={index}
              plantEnum={plantEnum}
              isSmall={deviceState.deviceWidth > 800}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default PlantEnums;
